import { mascararValor } from "@/lib/modo-valores";

export const UNIDADES = ["g", "kg", "ml", "L", "un", "Real"] as const;

/** Todo valor monetário passa pelo modo de visualização do usuário. */
const mv = (v: number) => mascararValor(Number.isFinite(v) ? v : 0);

export const brl = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(mv(v));

export const brl3 = (v: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(mv(v));

export const brl4 = (v: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(mv(v));

export const num = (v: number, casas = 2) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: casas }).format(
    Number.isFinite(v) ? v : 0,
  );

/**
 * Exibe uma quantidade com sua unidade. Em relatórios, cards e mensagens,
 * valores em gramas a partir de 1000 são convertidos para kg com 3 casas.
 */
export const medida = (v: number, unidade?: string | null, casas = 3) => {
  const n = Number.isFinite(v) ? v : 0;
  if (unidade === "g" && Math.abs(n) >= 1000) return `${num(n / 1000, 3)} kg`;
  if (unidade === "ml" && Math.abs(n) >= 1000) return `${num(n / 1000, 2)} l`;
  return `${num(n, casas)}${unidade ? ` ${unidade}` : ""}`;
};

/**
 * Exibe quantidade em alertas de estoque baixo na própria unidade do insumo,
 * sem qualquer conversão (g continua g, ml continua ml).
 */
export const medidaAlerta = (v: number, unidade?: string | null) => {
  const n = Number.isFinite(v) ? v : 0;
  return `${num(n, 3)}${unidade ? ` ${unidade}` : ""}`;
};

export const tipoLabel: Record<string, string> = {
  compra: "Compra / recebimento",
  transferencia: "Transferência entre estoques 3",
  perda: "Perda / estrago",
  venda: "Baixa por venda",
  ajuste: "Ajuste manual",
};

const TZ = "America/Sao_Paulo";

/** Data no padrão brasileiro: dd/mm/aaaa */
export const dataBR = (valor: string | Date) => {
  const d =
    typeof valor === "string"
      ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(valor) ? valor + "T00:00:00" : valor)
      : valor;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
};

/** Data e hora no padrão brasileiro: dd/mm/aaaa HH:MM:SS */
export const dataHoraBR = (valor: string | Date) => {
  const d = typeof valor === "string" ? new Date(valor) : valor;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(d);
};

/** Data e hora com o ano apenas quando for diferente do ano atual. */
export const dataHoraCurtaBR = (valor: string | Date) => {
  const d = typeof valor === "string" ? new Date(valor) : valor;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "—";
  const anoAtual = Number(
    new Intl.DateTimeFormat("pt-BR", { timeZone: TZ, year: "numeric" }).format(new Date()),
  );
  const ano = Number(new Intl.DateTimeFormat("pt-BR", { timeZone: TZ, year: "numeric" }).format(d));
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: TZ,
    day: "2-digit",
    month: "2-digit",
    ...(ano !== anoAtual ? { year: "numeric" as const } : {}),
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(d);
};

/** Converte texto digitado em pt-BR ("1.234,56") para número */
export const parseNum = (v: string | number) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v).trim().replace(/\s/g, "").replace(/\./g, "").replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Prepara um número vindo do banco para um campo de texto no padrão brasileiro
 * (vírgula como separador decimal). Evita que 14.90 seja lido como 1490/149.
 */
export const paraCampo = (v: number | string | null | undefined, casas = 0) => {
  const n = typeof v === "number" ? v : Number(String(v ?? "").replace(",", "."));
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
};

/** Data de hoje (São Paulo) no formato aaaa-mm-dd para inputs */
export const hojeISO = () => new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(new Date());

/** Todas as operações possíveis de lançamento no estoque */
export const TODAS_TABS = [
  { v: "compra", l: "Compra recebida" },
  { v: "transferencia", l: "Transferir entre estoques" },
  { v: "perda", l: "Perda / estrago" },
  { v: "ajuste", l: "Ajuste de estoque" },
] as const;
